import { Box, Skeleton, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import { CreditsOverview } from 'src/dto/Credits';
import useGetCurrency from 'src/frontend-common/hooks/useGetCurrency';
import { UsageCostWrapper } from 'src/pages/Welcome/style';
import { dotsFormat, fixedDigitsFormat } from 'src/utils/number';

interface ProductList {
  Onboarding: number;
  'Phone number': number;
  'Email Address': number;
  'IP Address': number;
  Device: number;
  Browser: number;
  Name: number;
}

const CostUsageTable: React.FC<{
  usage: CreditsOverview;
  cost: CreditsOverview;
  setSumOfCost: React.Dispatch<React.SetStateAction<number | null>>;
}> = ({ usage, cost, setSumOfCost }) => {
  const returnValueOrZero = (inilObj: CreditsOverview, keyLabel: string): number => {
    // for every value in the object, if the keyLabel exist in this value values return the value's value, otherwise return 0
    const value = Object.values(inilObj).map((el) => {
      if (el[keyLabel]) {
        return el[keyLabel];
      }
      return 0;
    });

    return value.reduce((a, b) => a + b);
  };
  const usageProducts: ProductList = useMemo(
    () => ({
      Onboarding: returnValueOrZero(usage, 'onboarding'),
      'Phone number': returnValueOrZero(usage, 'phone'),
      'Email Address': returnValueOrZero(usage, 'email'),
      'IP Address': returnValueOrZero(usage, 'ip'),
      Device: returnValueOrZero(usage, 'device'),
      Browser: returnValueOrZero(usage, 'browser'),
      Name: returnValueOrZero(usage, 'name'),
      Domain: returnValueOrZero(usage, 'domain'),
      Login: returnValueOrZero(usage, 'login'),
    }),
    [usage]
  );

  const costproducts: ProductList = useMemo(
    () => ({
      Onboarding: returnValueOrZero(cost, 'onboarding'),
      'Phone number': returnValueOrZero(cost, 'phone'),
      'Email Address': returnValueOrZero(cost, 'email'),
      'IP Address': returnValueOrZero(cost, 'ip'),
      Device: returnValueOrZero(cost, 'device'),
      Browser: returnValueOrZero(cost, 'browser'),
      Name: returnValueOrZero(cost, 'name'),
      Domain: returnValueOrZero(cost, 'domain'),
      Login: returnValueOrZero(cost, 'login'),
    }),
    [cost]
  );

  setSumOfCost(Object.values(costproducts).reduce((a, b) => a + b));
  return (
    <UsageCostWrapper testId="usage-cost-table">
      <Typography variant="h3">Total Usage</Typography>
      <Box>
        <Box>Product</Box>
        <Box>Lookups</Box>
        <Box>Amount</Box>
      </Box>
      <>
        {Object.keys(usageProducts).map((prodLabel: string, i: number) => (
          <Box key={prodLabel}>
            <Box>
              <Typography variant="body2">{prodLabel}</Typography>
            </Box>
            <Box>
              <Typography variant="body2">{dotsFormat(Object.values(usageProducts)[i] ?? 0)}</Typography>
            </Box>
            <Box>
              <Typography variant="body2">{`${useGetCurrency()} ${fixedDigitsFormat(Object.values(costproducts)[i], 2)}`}</Typography>
            </Box>
          </Box>
        ))}
      </>
    </UsageCostWrapper>
  );
};

export default CostUsageTable;

export const CostAndUsageSkeleton: React.FC = () => (
  <UsageCostWrapper testId="usage-cost-table-skeleton">
    <Typography variant="h3">Total Usage</Typography>
    <Box>
      <Box>Product</Box>
      <Box>Lookups</Box>
      <Box>Amount</Box>
    </Box>
    <>
      {[...Array(9)].map((prodLabel: string, i: number) => (
        <Box key={`${prodLabel}-${i}`}>
          <Box>
            <Typography variant="body2">
              <Skeleton width={65} />
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2">
              <Skeleton width={35} />
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2">
              <Skeleton width={85} />
            </Typography>
          </Box>
        </Box>
      ))}
    </>
  </UsageCostWrapper>
);
